import { getAcquisition } from "../shared/helpers/acquisition.helper";
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { Employee, EvaCompany, User } from "src/app/domains/internal";
import { environment } from "src/environments/environment";

import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";

import { EmployeesService } from "../features/employees/services/employees.service";

import { EmailTokenValidateDto } from "../core/auth/dtos/email-token-validate.dto";
import { RequestCallbackInputDto, SignUpInputDto, SignUpOutputDto } from "../core/auth/dtos/signup.dto";

/**
 * Clés localStorage à conserver lors du logout.
 * Ce sont des préférences UI utilisateur qui ne sont pas liées à la session.
 */
const PERSISTENT_LOCALSTORAGE_KEYS = [
  "columnsQuotation",
  "truncateArticles",
  "packShowLinesWithoutQuantity",
  "showTTC",
];

const PERSISTENT_LOCALSTORAGE_PREFIXES = ["eva-table-columns-"];

export interface OtpSentResponse {
  status: "otp_sent";
  dev_otp_code?: string;
}

/**
 * Efface le localStorage en préservant les préférences UI persistantes.
 */
export function clearLocalStorageKeepPreferences(): void {
  const preserved: Record<string, string> = {};
  const prefixedKeys = Object.keys(localStorage).filter((key) =>
    PERSISTENT_LOCALSTORAGE_PREFIXES.some((prefix) => key.startsWith(prefix)),
  );
  for (const key of [...PERSISTENT_LOCALSTORAGE_KEYS, ...prefixedKeys]) {
    const value = localStorage.getItem(key);
    if (value !== null) {
      preserved[key] = value;
    }
  }

  localStorage.clear();

  for (const [key, value] of Object.entries(preserved)) {
    localStorage.setItem(key, value);
  }
}

@Injectable({ providedIn: "root" })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User> = null;
  private newUserMail: any;
  private _isLoggingOut = false;
  private devLoginOtp: { email: string; code: string } | null = null;

  get isLoggingOut(): boolean {
    return this._isLoggingOut;
  }

  get jwt(): string {
    return localStorage.getItem("evaJwt");
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private injector: Injector,
  ) {
    // Si on revient d'un reload déclenché par le logout,
    // on nettoie tout AVANT que les autres services singleton ne démarrent.
    const pendingLogout = sessionStorage.getItem("LOGGING_OUT") === "true";
    if (pendingLogout) {
      sessionStorage.removeItem("LOGGING_OUT");
      clearLocalStorageKeepPreferences();
      sessionStorage.clear();
      this.currentUserSubject = new BehaviorSubject<User>(null);
    } else {
      this.currentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem("evaUser")));
    }
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  getCurrentUser(): User {
    if ("evaUser" in localStorage) {
      return JSON.parse(localStorage.getItem("evaUser"));
    } else {
      clearLocalStorageKeepPreferences();
      sessionStorage.clear();
      this.router.navigate(["/login"]);
      return null;
    }
  }

  login(username: string, password: string) {
    this._isLoggingOut = false;
    this.devLoginOtp = null;
    return this.http
      .post<OtpSentResponse>(`${environment.apiUrl}/auth/v2/login`, {
        email: username,
        password,
      })
      .pipe(
        tap((response) => {
          if (response.status === "otp_sent" && /^\d{6}$/.test(response.dev_otp_code ?? "")) {
            this.devLoginOtp = { email: username.toLowerCase(), code: response.dev_otp_code };
          }
        }),
      );
  }

  consumeDevLoginOtp(email: string): string {
    const otp = this.devLoginOtp;
    this.devLoginOtp = null;
    return otp && otp.email === email?.toLowerCase() ? otp.code : "";
  }

  reSEndValidationCode(username: string) {
    return this.http.post<OtpSentResponse>(`${environment.apiUrl}/auth/v2/login/re-send-validation-code`, {
      email: username,
    });
  }

  onBoardingFirstlogin(evaCompany: EvaCompany, employee: Employee) {
    return this.http
      .post<any>(`${environment.apiUrl}/auth/login/on_boarding`, {
        evaCompany: evaCompany,
        employee: employee,
      })
      .pipe(
        map((res) => {
          if (res.status === "PENDING") {
            this.logout();
            return res;
          }
          localStorage.setItem("evaJwt", res.token);
          const user = User.createInstance(res.user);
          localStorage.setItem("evaUser", JSON.stringify(user));
          this.currentUserSubject.next(user);
          this.clearStorage("currentEvaCompany");
          this.clearStorage("currentEvaCompaniesAvailable");
          this.logout();
          this.router.navigate(["/login"]);
        }),
      );
  }

  askSignUp(dto: SignUpInputDto): Observable<SignUpOutputDto> {
    return this.http.post<SignUpOutputDto>(`${environment.apiUrl}/auth/signup`, {
      ...dto,
      marketing: getAcquisition(),
    });
  }

  requestCallback(dto: RequestCallbackInputDto): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/auth/request-callback`, { ...dto, marketing: getAcquisition() });
  }
  searchSwissCompanies(name) {
    return this.http.post(`${environment.apiUrl}/auth/search_swiss_company`, {
      name: name,
    });
  }

  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/auth/ask_reset_password`, { email });
  }

  getSwissCompanyDetails(uid: string) {
    return this.http.get(`${environment.apiUrl}/auth/search_swiss_company/${uid}`);
  }
  searchFrenchCompanies(name) {
    return this.http.post(`${environment.apiUrl}/auth/search_french_company`, {
      denominationUniteLegale: name,
    });
  }

  validateToken(email, validationCode): Observable<EmailTokenValidateDto> {
    return this.http
      .post<EmailTokenValidateDto>(`${environment.apiUrl}/auth/email_token/validate`, {
        validationCode: validationCode,
        email: email,
      })
      .pipe(
        map((res: EmailTokenValidateDto) => {
          if (res.status === "PENDING") return res;
          localStorage.setItem("evaJwt", res.token);
          localStorage.setItem("evaRefreshJwt", res.refresh_token);

          const eva_company = EvaCompany.createInstance(res.eva_company);
          localStorage.setItem("currentEvaCompany", JSON.stringify(eva_company));
          // Stocke uniquement les champs minimaux pour le sélecteur (réduction quota localStorage)
          const lightCompany = {
            eva_company_id: eva_company.eva_company_id,
            name: eva_company.name,
            name_initials: eva_company.name_initials ?? "",
            company_color: eva_company.company_color ?? "",
            country: eva_company.country ?? "CH",
            primaryCurrency: eva_company.primaryCurrency ?? "CHF",
          };
          localStorage.setItem("currentEvaCompaniesAvailable", JSON.stringify([lightCompany]));

          const user = User.createInstance(res.user);
          this.updateUser(user);

          return res;
        }),
      );
  }

  updateUser(user: User) {
    localStorage.setItem("evaUser", JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  validateLoginOtp(email: string, otp: string): Observable<any> {
    return this.http
      .post(`${environment.apiUrl}/auth/login/validate_otp`, {
        email: email,
        validationCode: otp,
      })
      .pipe(
        tap((res: any) => {
          if (res.access_token && res.refresh_token) {
            localStorage.setItem("evaJwt", res.access_token);
            localStorage.setItem("evaRefreshJwt", res.refresh_token);
            const user = User.createInstance(res.user);
            localStorage.setItem("evaUser", JSON.stringify(user));
            this.currentUserSubject.next(user);
            this.clearStorage("currentEvaCompany");
            this.clearStorage("currentEvaCompaniesAvailable");
          }
        }),
      );
  }

  createUser(user: User): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/auth/register_user`, user).pipe(map(User.createInstance));
  }

  logout(reason: string = "") {
    this.devLoginOtp = null;
    this._isLoggingOut = true;
    this.injector.get(EmployeesService).clearSession();
    clearLocalStorageKeepPreferences();
    sessionStorage.clear();
    if (reason === "passwordExpired") {
      localStorage.setItem("LOGOUT_PASSWORD_EXPIRED", "true");
    }
    this.currentUserSubject.next(null);
  }

  clearStorage(key: string) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }

  setUserMail(data: any) {
    this.newUserMail = data;
  }

  getUserMail() {
    return this.newUserMail;
  }
  refreshToken() {
    return this.http.post<any>(`${environment.apiUrl}/auth/refresh`, {}).pipe(
      map((res) => {
        localStorage.setItem("evaJwt", res.token);
        return res.token;
      }),
    );
  }
}
